import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, ShoppingBag, Ruler, Tag, Check, Share2 } from "lucide-react";
import { products, WHATSAPP_NUMBER } from "@/data/products";
import { SEOHead } from "@/components/SEOHead";
import { productSchema, breadcrumbSchema } from "@/data/schemas";

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [currentImg, setCurrentImg] = useState(0);
  const product = products.find((p) => p.id === productId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Product not found</h2>
          <Link to="/shop" className="text-[#C8A23A] hover:text-[#7A1A31] transition-colors text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi! I'm interested in ordering "${product.name}" (${product.size}). Can you share pricing and availability?`)}`;
  const whatsappBulk = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi! I'd like to place a bulk order for "${product.name}" with customisation. Please share details.`)}`;

  const relatedProducts = products
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const nextImg = () => setCurrentImg((prev) => (prev + 1) % product.images.length);
  const prevImg = () => setCurrentImg((prev) => (prev - 1 + product.images.length) % product.images.length);

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: product.name, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="product-page">
      <SEOHead
        title={`${product.name} - Eagle Diaries ${product.year}`}
        description={`${product.description} Size: ${product.size}. Available for bulk orders with gold foiling, personalised name and page insertions. Order on WhatsApp.`}
        path={`/shop/${product.id}`}
        image={product.images[0]}
        type="product"
        schema={[
          productSchema(product),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Shop", url: "/shop" },
            { name: product.categoryLabel, url: `/shop?category=${product.category}` },
            { name: product.name },
          ]),
        ]}
      />
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAFAF7]/95 border-b border-black/5">
        <div className="container mx-auto px-6 sm:px-8 max-w-7xl flex items-center justify-between h-16 sm:h-20">
          <button
            onClick={() => navigate(-1)}
            data-testid="product-back-btn"
            className="flex items-center gap-3 text-[#6B7280] hover:text-[#7A1A31] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm hidden sm:inline" style={{ fontFamily: "'Outfit', sans-serif" }}>Back</span>
          </button>
          <Link to="/">
            <img src="/eagle_logo_cropped.png" alt="Emcee Sons" className="h-10 sm:h-12 w-auto" />
          </Link>
          <Link
            to="/shop"
            data-testid="product-to-shop"
            className="text-sm text-[#6B7280] hover:text-[#7A1A31] transition-colors"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            All Products
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl py-5">
        <nav className="flex items-center gap-2 text-xs text-[#6B7280]/50" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <Link to="/" className="hover:text-[#7A1A31] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#7A1A31] transition-colors">Shop</Link>
          <span>/</span>
          <Link to={`/shop?category=${product.category}`} className="hover:text-[#7A1A31] transition-colors">{product.categoryLabel}</Link>
          <span>/</span>
          <span className="text-[#6B7280]">{product.name}</span>
        </nav>
      </div>

      {/* Product Detail */}
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Main Image */}
            <div className="relative bg-white border border-black/5 rounded-xl overflow-hidden aspect-square mb-4">
              <img
                src={product.images[currentImg]}
                alt={product.name}
                data-testid="product-main-image"
                className="w-full h-full object-cover"
              />
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImg}
                    data-testid="product-img-prev"
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 border border-black/5 flex items-center justify-center text-[#1A1A1A]/70 hover:text-[#7A1A31] hover:bg-black/60 transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImg}
                    data-testid="product-img-next"
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 border border-black/5 flex items-center justify-center text-[#1A1A1A]/70 hover:text-[#7A1A31] hover:bg-black/60 transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {/* Year badge */}
              <div className="absolute top-4 left-4">
                <span
                  className="text-[10px] tracking-[0.1em] uppercase bg-[#D4AF37]/90 text-[#0B1021] px-3 py-1.5 rounded-xl font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {product.year}
                </span>
              </div>
            </div>

            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImg(idx)}
                    data-testid={`product-thumb-${idx}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      idx === currentImg ? "border-[#D4AF37]" : "border-black/5 hover:border-black/10"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col"
          >
            <span
              className="text-xs tracking-[0.15em] uppercase text-[#C8A23A] font-medium mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {product.categoryLabel}
            </span>

            <h1
              className="text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
              data-testid="product-title"
            >
              {product.name}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm text-[#6B7280]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Ruler className="w-4 h-4 text-[#C8A23A]/60" />
                {product.size}
              </div>
              <div className="flex items-center gap-2 text-sm text-[#6B7280]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Tag className="w-4 h-4 text-[#C8A23A]/60" />
                Eagle Diaries {product.year}
              </div>
            </div>

            {/* Description */}
            <p
              className="text-base text-[#6B7280] leading-relaxed mb-8"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
              data-testid="product-description"
            >
              {product.description}
            </p>

            {/* Features */}
            <div className="mb-10">
              <h3
                className="text-sm text-[#1A1A1A] mb-4"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              >
                Key Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3" data-testid={`product-feature-${idx}`}>
                    <div className="w-5 h-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#C8A23A]" />
                    </div>
                    <span className="text-sm text-[#6B7280]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customisation info */}
            <div className="bg-white border border-black/5 rounded-xl p-6 mb-8">
              <h4 className="text-sm text-[#1A1A1A] mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                Customisation Available
              </h4>
              <p className="text-xs text-[#6B7280] leading-relaxed mb-0" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
                Gold foiling with company logo, personalised names, page insertions, and fully bespoke editions for orders of 2000+.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mt-auto">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-order-btn"
                className="flex-1 flex items-center justify-center gap-3 bg-[#7A1A31] text-white px-8 py-4 rounded-xl hover:bg-[#5C162E] transition-all duration-300 text-sm font-medium tracking-wide"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                <ShoppingBag className="w-4 h-4" />
                Order Now
              </a>
              <a
                href={whatsappBulk}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-bulk-btn"
                className="flex-1 flex items-center justify-center gap-3 border border-[#D4AF37]/40 text-[#C8A23A] px-8 py-4 rounded-xl hover:bg-[#D4AF37]/10 transition-all duration-300 text-sm font-medium tracking-wide"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Bulk Order Enquiry
              </a>
              <button
                onClick={handleShare}
                data-testid="product-share-btn"
                className="w-12 h-12 flex items-center justify-center border border-black/5 rounded-xl text-[#6B7280] hover:text-[#7A1A31] hover:border-black/10 transition-all flex-shrink-0"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <div className="flex items-center justify-between mb-8">
              <h2
                className="text-xl sm:text-2xl text-[#1A1A1A]"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
              >
                More in {product.categoryLabel}
              </h2>
              <Link
                to={`/shop?category=${product.category}`}
                className="text-sm text-[#C8A23A] hover:text-[#7A1A31] transition-colors"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                View All
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {relatedProducts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/shop/${rp.id}`}
                  data-testid={`related-${rp.id}`}
                  className="group block bg-white border border-black/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all"
                >
                  <div className="aspect-square bg-[#F5F5F3] overflow-hidden">
                    <img src={rp.images[0]} alt={rp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm text-[#1A1A1A] truncate" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                      {rp.name}
                    </h4>
                    <span className="text-xs text-[#6B7280]/50" style={{ fontFamily: "'Outfit', sans-serif" }}>{rp.size}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
